import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Subject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { environment } from '../../../environment/environment';
import { ConfirmResetDialogComponent } from '../duna-dashboard/confirm-reset-dialog.component';

interface UniqueLinkUser {
  user_id: number;
  user_uuid: string | null;
  name: string;
  email: string | null;
  link_uuid: string | null;
  link_path: string | null;
  educational_paths?: Array<{ path_id: number; name: string }>;
}

@Component({
  selector: 'app-duna-catering-unique-links',
  standalone: true,
  imports: [
    FormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    MatDialogModule,
  ],
  templateUrl: './duna-catering-unique-links.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-catering-unique-links.component.scss',
})
export class DunaCateringUniqueLinksComponent implements OnInit, OnDestroy {
  searchTerm = '';
  searching = false;
  creating = false;
  deletingUserId: number | null = null;
  errorMessage: string | null = null;
  copiedLink: string | null = null;

  searchResults: UniqueLinkUser[] = [];
  /** Material table opdaterer mest stabilt via MatTableDataSource (ikke rå array). */
  linkedTableDataSource = new MatTableDataSource<UniqueLinkUser>([]);
  displayedColumns = ['name', 'email', 'link', 'actions'];
  private searchTerm$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.searchTerm$
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((term) => {
          if (term.length < 2) {
            this.searchResults = [];
            this.searching = false;
            return of({ items: [] as UniqueLinkUser[] });
          }
          this.searching = true;
          this.errorMessage = null;
          return this.http
            .get<{ items: UniqueLinkUser[] }>(
              `${environment.apiUrl}/events/catering/unique-links`,
              {
                params: { search: term, limit: 50 as any },
              }
            )
            .pipe(
              catchError((error) => {
                this.errorMessage = this.formatApiError(
                  error,
                  'Kunne ikke søge brugere.'
                );
                console.error(
                  '[duna-catering-unique-links] search failed',
                  error
                );
                return of({ items: [] as UniqueLinkUser[] });
              })
            );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((response) => {
        this.searchResults = response?.items || [];
        this.searching = false;
      });

    this.loadLinkedUsers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  searchUsers(): void {
    const term = this.searchTerm.trim();
    this.searchTerm$.next(term);
  }

  onSearchTermChange(value: string): void {
    this.searchTerm$.next((value || '').trim());
  }

  createOrGetLink(user: UniqueLinkUser): void {
    if (!user.user_uuid) return;
    this.creating = true;
    this.errorMessage = null;

    this.http
      .post<UniqueLinkUser>(
        `${environment.apiUrl}/events/catering/unique-links`,
        {
          user_uuid: user.user_uuid,
        }
      )
      .subscribe({
        next: (created) => {
          this.creating = false;
          this.upsertLinkedUser(created);
          this.searchResults = this.searchResults.map((item) =>
            item.user_id === created.user_id ? created : item
          );
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.creating = false;
          this.errorMessage = this.formatApiError(
            error,
            'Kunne ikke oprette unikt link.'
          );
          console.error('[duna-catering-unique-links] POST failed', error);
        },
      });
  }

  deleteLink(row: UniqueLinkUser): void {
    if (!row.link_uuid) return;

    const displayName = row.name?.trim() || row.email?.trim() || 'brugeren';
    const ref = this.dialog.open(ConfirmResetDialogComponent, {
      width: 'min(520px, 92vw)',
      data: {
        title: 'Slet forplejningslink?',
        message: `Du er ved at tilbagekalde det offentlige forplejningslink for ${displayName}.`,
        details: [
          'Alle med det gamle link mister adgang til forplejningssiden (/catering/…).',
          'Du kan oprette et nyt link til samme bruger senere fra listen eller via søgning.',
          'Brugeren slettes ikke, og medlemskab af forplejningsgrupper ændres ikke.',
        ],
        confirmText: 'Ja, slet link',
        cancelText: 'Annuller',
        confirmColor: 'warn',
      },
    });

    ref
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.errorMessage = null;
        this.deletingUserId = row.user_id;
        this.http
          .delete(
            `${
              environment.apiUrl
            }/events/catering/unique-links/${encodeURIComponent(
              row.link_uuid!
            )}`
          )
          .subscribe({
            next: () => {
              this.deletingUserId = null;
              this.linkedTableDataSource.data =
                this.linkedTableDataSource.data.filter(
                  (u) => u.user_id !== row.user_id
                );
              this.searchResults = this.searchResults.map((u) =>
                u.user_id === row.user_id
                  ? { ...u, link_uuid: null, link_path: null }
                  : u
              );
              this.cdr.detectChanges();
            },
            error: (error) => {
              this.deletingUserId = null;
              this.errorMessage = this.formatApiError(
                error,
                'Kunne ikke slette link.'
              );
              console.error(
                '[duna-catering-unique-links] DELETE failed',
                error
              );
              this.cdr.detectChanges();
            },
          });
      });
  }

  copyLink(item: UniqueLinkUser): void {
    const absolute = this.absoluteLink(item);
    if (!absolute) return;
    navigator.clipboard?.writeText(absolute).then(() => {
      this.copiedLink = absolute;
      setTimeout(() => {
        if (this.copiedLink === absolute) this.copiedLink = null;
      }, 2000);
    });
  }

  absoluteLink(item: UniqueLinkUser): string | null {
    if (!item.link_uuid) return null;
    return `${window.location.origin}/catering/${item.link_uuid}`;
  }

  educationalPathsLabel(item: UniqueLinkUser): string {
    const paths = item.educational_paths || [];
    if (!paths.length) return '';
    return paths
      .map((p) => p.name)
      .filter((name) => !!name)
      .join(', ');
  }

  trackByUserId(_: number, item: UniqueLinkUser): number {
    return item.user_id;
  }

  private loadLinkedUsers(): void {
    this.http
      .get<{ items: UniqueLinkUser[] }>(
        `${environment.apiUrl}/events/catering/unique-links`,
        {
          params: { limit: 200 as any },
        }
      )
      .subscribe({
        next: (response) => {
          this.linkedTableDataSource.data = (response?.items || []).filter(
            (item) => !!item.link_uuid
          );
          this.cdr.detectChanges();
        },
        error: (error) => {
          this.linkedTableDataSource.data = [];
          this.errorMessage = this.formatApiError(
            error,
            'Kunne ikke hente eksisterende links.'
          );
          console.error('[duna-catering-unique-links] list load failed', error);
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * RCMS database errors often return { message: 'Database error', error: '<SQL detail>' }.
   * PHP catch blocks may return { message, detail, exception }.
   */
  private formatApiError(error: unknown, fallback: string): string {
    const httpErr = error as {
      error?: unknown;
      message?: string;
      status?: number;
    };
    const body = httpErr?.error;

    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body) as Record<string, unknown>;
        if (parsed && typeof parsed === 'object') {
          return this.formatApiError({ ...httpErr, error: parsed }, fallback);
        }
      } catch {
        return body.trim() || fallback;
      }
    }

    if (body && typeof body === 'object') {
      const o = body as { message?: string; error?: string; detail?: string };
      const tech = (o.detail || o.error || '').trim();
      const msg = (o.message || '').trim();
      if (tech && msg) return `${msg}: ${tech}`;
      return tech || msg || fallback;
    }

    return (httpErr?.message || '').trim() || fallback;
  }

  private upsertLinkedUser(item: UniqueLinkUser): void {
    if (!item?.link_uuid) return;
    const rows = [...this.linkedTableDataSource.data];
    const idx = rows.findIndex((existing) => existing.user_id === item.user_id);
    if (idx >= 0) {
      rows[idx] = item;
    } else {
      rows.push(item);
      rows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }
    this.linkedTableDataSource.data = rows;
  }
}
